import {
    Box,
    Card,
    Grid,
    Icon,
    IconButton,
    InputLabel,
    ListItem,
    ListItemButton,
    ListItemText,
    Menu,
    MenuItem,
    Paper,
    TextField,
    useTheme,
} from "@mui/material";
import { format, parse } from "date-fns";
import { getReadOnly, store } from "./bidStore";
import { request } from "@/Api";
import { CloseCircle, MenuDots } from "@solar-icons/react/ssr";
import { useEffect, useRef, useState } from "react";
import ConfirmationDialog from "@/common/ConfirmationDialog";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { DateTimePicker } from "@mui/x-date-pickers";

export const BidHistory = ({ bid: paramBid, open, close, refreshBid }) => {
    const activeBid = paramBid || store.bid;
    const [selected, setSelected] = useState(null);
    const [transactions, setTransactions] = useState([]);
    const getTransactions = (issuedAtFrom = null, issuedAtTo = null) => {
        request
            .get(`/bids/${activeBid.id}/transaction`, {
                params: {
                    issued_at_from: issuedAtFrom,
                    issued_at_to: issuedAtTo,
                },
            })
            .then((response) => {
                setTransactions(response.data);
            });
    };
    const handleClose = () => {
        close();
        setSelected(null);
    };
    const handleDateRangeChange = (range) => {
        getTransactions(range.startDate, range.endDate);
    };
    useEffect(() => {
        if (open) {
            getTransactions();
        }
    }, [open]);
    return (
        <Grid
            container
            display={"flex"}
            flexDirection={"column"}
            sx={{ height: "100%" }}
        >
            <Grid item sx={{ overflowY: "auto" }} flex={1}>
                <div style={{ padding: 16, minWidth: 300 }}>
                    <Grid
                        container
                        alignItems={"center"}
                        justifyContent={"space-between"}
                    >
                        <Grid item>
                            <p>Versions</p>
                        </Grid>
                        <Grid item>
                            <IconButton onClick={handleClose}>
                                <CloseCircle />
                            </IconButton>
                        </Grid>
                    </Grid>
                    {transactions.map((transaction) => (
                        <BidTransaction
                            key={transaction.id}
                            bid={paramBid}
                            transaction={transaction}
                            refreshBid={refreshBid}
                            selected={selected}
                            setSelected={setSelected}
                        />
                    ))}
                </div>
            </Grid>
            <Grid item>
                <div style={{ margin: 16 }}>
                    <DateRange
                        onRangeChange={handleDateRangeChange}
                        // defaultStartDate={startDate}
                        // defaultEndDate={endDate}
                    />
                </div>
            </Grid>
        </Grid>
    );
};

const BidTransaction = ({
    bid: paramBid,
    transaction,
    refreshBid,
    selected,
    setSelected,
}) => {
    const theme = useTheme();
    const [open, setOpen] = useState(false);
    const [copyConfirmationPopupOpen, setCopyConfirmationPopupOpen] =
        useState(false);
    const [revertConfirmationPopupOpen, setRevertConfirmationPopupOpen] =
        useState(false);
    const listItemRef = useRef(null);
    const activeBid = paramBid || store.bid;
    const bidId = activeBid.id;
    const thisSelected = selected == transaction.id;
    const readOnly = getReadOnly();
    const handleGetVersion = () => {
        // TODO: implement revision preview
        request
            .get(`/bids/${bidId}/version`, {
                params: { issued_at: transaction.issued_at },
            })
            .then((response) => {
                setSelected(transaction.id);
                refreshBid(response.data);
            });
    };
    const handleRevertVersion = () => {
        request
            .post(`/bids/${bidId}/revert-version`, null, {
                params: { issued_at: transaction.issued_at },
            })
            .then((_) => {
                setOpen(false);
            });
    };
    const handleCopyVersion = () => {
        request
            .post(`/bids/${bidId}/copy-version`, null, {
                params: { issued_at: transaction.issued_at },
            })
            .then((response) => {
                setOpen(false);
                window.location = `/bids/${response.data.id}`;
                navigate(`/bids/${response.data.id}`, 0);
            });
    };
    return (
        <ListItem key={transaction.id} disablePadding>
            <Grid container alignItems={"center"} spacing={1}>
                <Grid item flexGrow={1} marginBottom={1}>
                    <Card>
                        <ListItemButton
                            style={{
                                background: thisSelected
                                    ? theme.palette.success.main
                                    : "inherit",
                            }}
                            onClick={handleGetVersion}
                        >
                            <ListItemText sx={{ padding: 0, margin: 0 }}>
                                <span>
                                    {format(
                                        new Date(transaction.issued_at + "Z"),
                                        "y-MM-dd h:mm:ss aaa",
                                    )}
                                </span>
                                <br />
                                <span style={{ fontSize: 12 }}>
                                    {transaction.person_name}
                                </span>
                            </ListItemText>
                        </ListItemButton>
                    </Card>
                </Grid>
                <Grid item>
                    <IconButton ref={listItemRef} onClick={() => setOpen(true)}>
                        <MenuDots />
                    </IconButton>
                </Grid>
            </Grid>
            <Menu
                anchorEl={listItemRef.current}
                open={open}
                onClose={() => setOpen(false)}
            >
                <MenuItem onClick={() => setCopyConfirmationPopupOpen(true)}>
                    Make a copy
                </MenuItem>
                <MenuItem onClick={() => setRevertConfirmationPopupOpen(true)}>
                    Revert to version
                </MenuItem>
            </Menu>
            <ConfirmationDialog
                open={copyConfirmationPopupOpen}
                closeDialog={() => setCopyConfirmationPopupOpen(false)}
                title="Copy version"
                callback={handleCopyVersion}
            >
                <p>Are you sure you want to copy this version?</p>
            </ConfirmationDialog>
            <ConfirmationDialog
                open={revertConfirmationPopupOpen}
                closeDialog={() => setRevertConfirmationPopupOpen(false)}
                title="Revert to version"
                callback={handleRevertVersion}
            >
                <p>Are you sure you want to revert to this version?</p>
            </ConfirmationDialog>
        </ListItem>
    );
};

const DateRange = ({ onRangeChange, defaultStartDate, defaultEndDate }) => {
    const [startDate, setStartDate] = useState(defaultStartDate);
    const [endDate, setEndDate] = useState(defaultEndDate);

    const [utcDates, setUtcDates] = useState({
        startDate: null,
        endDate: null,
    });

    const handleDateChange = (key, setDate) => (value) => {
        let parsed = null;
        let parsedUtc = null;
        if (!value || value == "Invalid Date") {
            setDate(null);
        }
        try {
            parsed = format(value, "y-MM-dd HH:mm");
            parsedUtc = format(
                new Date(value.getTime() + value.getTimezoneOffset() * 60000),
                "y-MM-dd HH:mm",
            );
            setDate(parsed);
        } catch (e) {
            // Keep empty string if invalid date
        }
        setUtcDates({ ...utcDates, [key]: parsedUtc });
        onRangeChange && onRangeChange({ ...utcDates, [key]: parsedUtc });
    };
    const handleClear = () => {
        setStartDate(null);
        setEndDate(null);
        setUtcDates({ startDate: null, endDate: null });
        onRangeChange && onRangeChange({ startDate: null, endDate: null });
    };
    return (
        <Grid container alignItems={"center"} spacing={1}>
            <Grid item>
                <Grid container alignItems={"center"} spacing={2}>
                    <Grid item width={50}>
                        <InputLabel sx={{ fontSize: 14 }}>From</InputLabel>
                    </Grid>
                    <Grid item flexGrow={1} paddingBottom={1}>
                        <LocalizationProvider dateAdapter={AdapterDateFns}>
                            <DateTimePicker
                                maxDate={
                                    endDate
                                        ? parse(
                                              endDate,
                                              "y-MM-dd HH:mm",
                                              new Date(),
                                          )
                                        : null
                                }
                                value={
                                    startDate
                                        ? parse(
                                              startDate,
                                              "y-MM-dd HH:mm",
                                              new Date(),
                                          )
                                        : null
                                }
                                inputFormat={"yyyy-MM-dd HH:mm"}
                                onChange={handleDateChange(
                                    "startDate",
                                    setStartDate,
                                )}
                                renderInput={(params) => (
                                    <TextField
                                        size="small"
                                        placeholder="From"
                                        fullWidth
                                        InputLabelProps={{ shrink: false }}
                                        {...params}
                                    />
                                )}
                            />
                        </LocalizationProvider>
                    </Grid>
                </Grid>
                <Grid container alignItems={"center"} spacing={2}>
                    <Grid item width={50}>
                        <InputLabel sx={{ fontSize: 14 }}>To</InputLabel>
                    </Grid>
                    <Grid item flexGrow={1}>
                        <LocalizationProvider dateAdapter={AdapterDateFns}>
                            <DateTimePicker
                                minDate={
                                    startDate
                                        ? parse(
                                              startDate,
                                              "y-MM-dd HH:mm",
                                              new Date(),
                                          )
                                        : null
                                }
                                value={
                                    endDate
                                        ? parse(
                                              endDate,
                                              "y-MM-dd HH:mm",
                                              new Date(),
                                          )
                                        : null
                                }
                                inputFormat={"yyyy-MM-dd HH:mm"}
                                onChange={handleDateChange(
                                    "endDate",
                                    setEndDate,
                                )}
                                renderInput={(params) => (
                                    <TextField
                                        size="small"
                                        placeholder="To"
                                        fullWidth
                                        InputLabelProps={{ shrink: false }}
                                        {...params}
                                    />
                                )}
                            />
                        </LocalizationProvider>
                    </Grid>
                </Grid>
            </Grid>
            <Grid item>
                <IconButton onClick={handleClear}>
                    <CloseCircle />
                </IconButton>
            </Grid>
        </Grid>
    );
};
